import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ArrowRightLeft, ArrowLeft, Send, PackageCheck, Ban, Tag } from 'lucide-react';
import { downloadBlob } from '../../utils/downloadBlob';
import { slotWhere } from '../../utils/nurseryRowSlots';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { TransferStatusBadge } from '../../components/nursery/despatch/TransferStatusBadge';
import type { ApiResponse } from '../../types/api';
import type { NurseryTransfer } from '../../types/nursery';

/**
 * /nursery/transfers/:id — one transfer. Send it when it leaves; receive it
 * when it arrives, with the count per lot. A shortfall is written into the
 * lot's movement, not lost; an allocation riding a line follows its trees.
 */

function TransferDetail() {
  const { id } = useParams<{ id: string }>();
  const transferId = Number(id);
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const [t, setT] = useState<NurseryTransfer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [counts, setCounts] = useState<Record<number, number | ''>>({});

  const load = useCallback(async () => {
    try {
      const res = await nurseryService.getTransfer(transferId);
      if (res.success) setT(res.data);
      else setError(errorMessage(res, 'Transfer not found'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the transfer'));
    }
  }, [transferId]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const act = async (fn: () => Promise<ApiResponse<NurseryTransfer>>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.success) { setT(res.data); return true; }
      setError(errorMessage(res, fallback));
    } catch (e) {
      setError(errorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
    return false;
  };

  if (!t) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        {error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <Link to="/nursery/transfers" className="text-sm text-primary-700 underline">Back to transfers</Link>
          </div>
        ) : <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />}
      </div>
    );
  }

  const open = t.status === 'planned' || t.status === 'in_transit';
  const lines = t.lines ?? [];

  const startReceive = () => {
    setCounts(Object.fromEntries(lines.map((l) => [l.id, l.quantity])));
    setReceiving(true);
  };

  const receive = async () => {
    const received: Record<number, number> = {};
    for (const l of lines) received[l.id] = counts[l.id] === '' ? 0 : Number(counts[l.id] ?? l.quantity);
    if (await act(() => nurseryService.receiveTransfer(transferId, received), 'Could not receive the transfer')) setReceiving(false);
  };

  return (
    <>
      <PageHeader
        title={`Transfer ${t.transfer_number}`}
        subtitle={`${t.from_farm_name} → ${t.to_farm_name} · ${formatLocalDateInTz(t.scheduled_date, tz)}`}
        icon={<ArrowRightLeft className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            <TransferStatusBadge status={t.status} />
            {t.status === 'planned' && !receiving && (
              <Button variant="outline" size="sm" disabled={busy} onClick={() => act(() => nurseryService.sendTransfer(transferId), 'Could not send the transfer')}>
                <Send className="h-4 w-4" /> Sent
              </Button>
            )}
            {open && !receiving && (
              <Button size="sm" disabled={busy} onClick={startReceive}>
                <PackageCheck className="h-4 w-4" /> Receive
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <button type="button" onClick={() => navigate('/nursery/transfers')} className="inline-flex items-center gap-1 text-sm text-primary-700">
          <ArrowLeft className="h-4 w-4" /> All transfers
        </button>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        <div className="bg-white border border-neutral-200 rounded-xl p-4 text-sm space-y-1">
          <p className="break-words"><span className="text-neutral-500">Into:</span> {t.to_location_name}, {t.to_farm_name}</p>
          {t.load_number && (
            <p><span className="text-neutral-500">For load:</span>{' '}
              <Link to={`/nursery/loads/${t.load_id}`} className="text-primary-700 underline">{t.load_number}</Link></p>
          )}
          {t.source === 'refill' && <p className="text-neutral-600">Raised by a despatch area running low.</p>}
          {t.vehicle && <p className="break-words"><span className="text-neutral-500">Vehicle:</span> {t.vehicle}</p>}
          {t.notes && <p className="text-neutral-600 break-words">{t.notes}</p>}
          {t.sent_at && <p className="text-neutral-500">Sent {formatDateTimeInTz(t.sent_at, tz)}</p>}
          {t.received_at && <p className="text-neutral-500">Received {formatDateTimeInTz(t.received_at, tz)}</p>}
        </div>

        {t.status === 'received' && lines.some((l) => l.to_lot_id && l.to_lot_id !== l.lot_id) && (
          <div className="flex flex-wrap items-center gap-2 bg-white border border-neutral-200 rounded-xl p-4">
            <p className="text-sm text-neutral-700 flex-1 min-w-0">Split lots have new lot numbers. Label them where they now stand.</p>
            <Button variant="outline" size="sm" disabled={busy} onClick={async () => {
              try {
                const ids = [...new Set(lines.map((l) => l.to_lot_id).filter((v): v is number => v != null))];
                downloadBlob(await nurseryService.getLotLabelsPdf(ids), `labels-${t.transfer_number}.pdf`);
              } catch (e) {
                setError(errorMessage(e, 'Could not make the labels'));
              }
            }}>
              <Tag className="h-4 w-4" /> Print labels
            </Button>
          </div>
        )}

        {receiving && (
          <p className="text-sm text-neutral-700">Count what arrived for each lot. Anything short is recorded against the lot it left.</p>
        )}

        <ul className="space-y-3">
          {lines.map((l) => {
            const short = l.received_quantity != null && l.received_quantity < l.quantity;
            return (
              <li key={l.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-neutral-900 break-words">{l.cultivar_name}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}</p>
                    <p className="text-sm text-neutral-600 break-words">
                      {[l.tree_type_name, l.lot_code ? `Lot ${l.lot_code}` : null].filter(Boolean).join(' · ')}
                    </p>
                    <p className="text-sm text-neutral-800 break-words">
                      {slotWhere(l)}
                    </p>
                    {l.order_number && <p className="text-xs text-neutral-500 break-words">For {l.order_number} · {l.customer_name}</p>}
                    {l.received_quantity != null && (
                      <p className={short ? 'text-sm text-amber-800 mt-1' : 'text-sm text-green-800 mt-1'}>
                        {l.received_quantity} arrived{short ? ` of ${l.quantity}` : ''}{l.to_lot_code && l.to_lot_code !== l.lot_code ? ` · now lot ${l.to_lot_code}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-bold text-neutral-900 leading-none">{l.quantity}</p>
                    <p className="text-xs text-neutral-500">{l.quantity === l.lot_count ? 'whole lot' : `of ${l.lot_count}`}</p>
                  </div>
                </div>
                {receiving && (
                  <div className="mt-3 flex items-center gap-2">
                    <label htmlFor={`rcv-${l.id}`} className="text-sm text-neutral-700">Arrived</label>
                    <input id={`rcv-${l.id}`} type="number" inputMode="numeric" min={0} max={l.quantity} value={counts[l.id] ?? ''}
                      onChange={(e) => setCounts((c) => ({ ...c, [l.id]: e.target.value === '' ? '' : Math.min(l.quantity, Math.max(0, Number(e.target.value))) }))}
                      className="w-24 border border-neutral-300 rounded-lg px-2 py-1.5 text-sm text-right" />
                    <span className="text-sm text-neutral-500">of {l.quantity}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {receiving && (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setReceiving(false)} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={receive} disabled={busy}>{busy ? 'Receiving…' : 'Record as received'}</Button>
          </div>
        )}

        {open && canManage && !receiving && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" disabled={busy}
              onClick={() => { if (window.confirm('Cancel this transfer? Nothing will have moved.')) act(() => nurseryService.cancelTransfer(transferId), 'Could not cancel the transfer'); }}>
              <Ban className="h-4 w-4" /> Cancel transfer
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

export default TransferDetail;
